import { Component, OnInit, OnDestroy, ViewChildren, QueryList, AfterViewInit, inject } from "@angular/core";
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { Role } from "src/app/models/admin/role";
import { UserV2 } from "src/app/models/admin/user-v2";
import { AdminService } from "src/app/services/admin.service";
import { FileGroup } from "src/app/models/admin/file-group";
import { TableData } from "src/app/models/table-data";
import { TablePermission } from "src/app/models/admin/permissions/table-permission";
import { IntegrationDetails } from "src/app/models/integration-details";
import { FileGroupPermission } from "src/app/models/admin/permissions/file-group-permission";
import { AuthService } from "src/app/services/auth-service.service";
import { ToastService } from 'src/app/services/toast.service';
import { StringLocalizerService } from "src/app/services/string-localizer.service";
import { ConfigService } from "src/app/services/config.service";
import {
  MatDialog,
} from '@angular/material/dialog';
import { ConfirmationDialogComponent } from "../dialogs/confirmation-dialog/confirmation-dialog.component";
import { CollapsibleComponent } from "../collapsible/collapsible.component";
import { LoggerService } from "src/app/services/logger.service";

@Component({
  selector: 'app-admin',
  templateUrl: './admin.component.html',
  styleUrls: ['./admin.component.css'],
  standalone: false
})

export class AdminComponent implements OnInit, OnDestroy, AfterViewInit {
  private readonly logger = inject(LoggerService);
  // Access Control
  private static readonly REQUIRED_ADMIN_FEATURE = 'DISDashboardAdmin';
  private static readonly REQUIRE_AUTHZ_FEATURES = 'Dashboard:RequireAuthZ';
  public hasAdminAccess: boolean = false;
  public isCheckingAccess: boolean = true;

  // i18n
  public strings: {} = {};

  // Child component controls
  @ViewChildren('tableCollapsible') tableCollapsibles!: QueryList<CollapsibleComponent>;
  @ViewChildren('integrationCollapsible') integrationCollapsibles!: QueryList<CollapsibleComponent>;

  // UI State Varibles
  public selectedTab: string = "Permissions"  // Accepted values are "Permissions" and "FileGroups"
  public selectedUser: UserV2;
  public selectedRole: Role | null = null;
  public selectedFile: File[];
  public showUserSyncButton: boolean = false;
  public isSyncing: boolean = false;
  public disableButtons: boolean = true;
  public isSaving: boolean = false;
  public showSaveButton: boolean = false;
  public editIndex: string | null = null;
  public editRoleNameEnable: boolean = false
  public enableEditing: boolean = false;
  public oneOwner: boolean = false;
  public localHost: boolean = false;
  public notProd: boolean = false;
  public collapsibleTitleColor1: string = "#c7dafda8";
  public collapsibleTitleColor2: string = "#c7dafd31";
  public collapsibleTitleFontSize: string = "1.5rem";
  public collapsibleTitleFontWeight: string = "bold";
  public collapsibleSubTitleFontWeight: number = 300;

  // Search Strings + Filtered Data
  public searchStringPermission: string = '';
  public searchStringFileGroup: string = '';
  public searchStringRole: string = '';
  public searchStringAssign: string = '';
  public searchStringAvaliable: string = '';
  public searchStringUser: string = '';
  public filteredIntegrations: IntegrationDetails[] = [];
  public filteredTables: TablePermission[] = [];
  public filteredAssignedFiles: FileGroup[] = [];
  public filteredAvailableFiles: FileGroup[] = [];
  public filteredRoles: Role[] = [];
  public filteredUsers: UserV2[] = [];
  public nonMatchingUsers: UserV2[] = [];
  public matchingUsers: UserV2[] = [];

  // Model Data
  public fileGroups: FileGroup[] = [];
  public users: UserV2[] = [];
  public roles: Role[] = [];
  public initialRoles: Role[] = [];
  public tables: TablePermission[] = [];
  public integrations: IntegrationDetails[] = [];
  public availableFileGroups: FileGroup[] = [];
  public assignedFileGroups: FileGroup[] = [];
  private userId: string;

  // Model Change Tracking
  public updatedUsers: UserV2[] = [];
  public updatedRoles: Role[] = [];
  private originalRoleNameBeforeEdit: string | null = null;
  public editRoleNameDraft: string = '';

  // Constructor ===========================================================================================
  private readonly destroy$ = new Subject<void>();

  constructor(private authService: AuthService, private adminService: AdminService, private toastService: ToastService, private stringLocalizerService: StringLocalizerService, private configService: ConfigService, private dialog: MatDialog) {

    if (this.configService.getEnvironment() !== "PROD") {
      this.notProd = true;
    }
  }


  // Startup ===========================================================================================
  ngOnInit(): void {
    this.bindStreams();
    void this.initialize().catch(err => this.logger.error('AdminComponent', 'Admin initialization failed', err, {
      operation: 'initialize'
    }));
  }

  ngAfterViewInit(): void {
    this.tableCollapsibles?.changes?.pipe(takeUntil(this.destroy$)).subscribe(() => {
    });

    this.integrationCollapsibles?.changes?.pipe(takeUntil(this.destroy$)).subscribe(() => {
    });
  }

  private bindStreams(): void {
    this.authService.$userClaims.pipe(takeUntil(this.destroy$)).subscribe(claims => {
      if (claims != null && claims.length > 0) {
        const userIdClaim = claims.find(c => c.type === "df.userid");
        this.userId = userIdClaim?.value ?? "UNKNOWN_USER_ID";
      }
    });
    this.adminService.$myAccess.pipe(takeUntil(this.destroy$)).subscribe(access => {
      if (access.flags.find(x => x.name === "Dashboard:UserSync" && x.enabled)) {
        this.showUserSyncButton = true;
      }
    })
    this.adminService.$disableAdminButtons.pipe(takeUntil(this.destroy$)).subscribe(disableButtons => {
      this.disableButtons = disableButtons;
    });
    this.adminService.$updatedRoles.pipe(takeUntil(this.destroy$)).subscribe(roles => {
      if (roles.length > 0) {
        this.showSaveButton = true;
      }
    });
    this.adminService.$selectedRole.pipe(takeUntil(this.destroy$)).subscribe(role => {
      this.selectedRole = role;
    });
    this.stringLocalizerService.strings.pipe(takeUntil(this.destroy$)).subscribe(s => {
      if (s) {
        this.strings = this.stringLocalizerService.getSection("admin");
      }
    });
  }

  private async initialize(): Promise<void> {
    // Component-level authorization (Requirement 2): only DIS Dashboard Admin can use this component.
    try {
      // authGuard for the app may have already loaded this, but calling again is safe.
      await this.adminService.loadMyAccess();
      const features = this.adminService.myAccess?.userFeatures ?? [];

      const flag = this.adminService.myAccess?.flags?.find(f => f.name === AdminComponent.REQUIRE_AUTHZ_FEATURES);
      const requireAuthZ = flag ? flag.enabled : false;
      this.hasAdminAccess = this.adminService.myAccess && (!requireAuthZ || features.includes(AdminComponent.REQUIRED_ADMIN_FEATURE));

      if (!this.hasAdminAccess) {
        return;
      }

      await this.initializeData();
    } finally {
      this.isCheckingAccess = false;
    }
  }

  private async initializeData(): Promise<void> {
    await Promise.all([
      this.adminService.loadUsers(),
      this.adminService.loadRoles(),
      this.adminService.loadTableData(),
      this.adminService.loadFileGroups(),
      this.adminService.loadIntegrations(),
    ]);

    this.users = this.adminService.users;
    this.roles = this.adminService.roles;
    this.tables = this.adminService.tables.map(t => this.toTablePermission(t));
    this.fileGroups = this.adminService.fileGroups;
    this.integrations = this.adminService.integrations;

    this.initialRoles = JSON.parse(JSON.stringify(this.roles));
    this.filteredUsers = [...this.users];
    this.filteredRoles = [...this.roles];
    this.filteredTables = [...this.tables]
    this.filteredIntegrations = [...this.integrations];
  }


  // Tear Down ===========================================================================================
  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  // State management ===========================================================================================
  selectUser(user) {
    this.selectedUser = user;
    this.calculateEnabledRoles();
    this.checkForOwners();
  }

  async selectRole(role) {
    if (this.enableEditing && this.editIndex !== role.id) {
      this.cancelRoleNameEdit();
    }

    this.adminService.selectRole(role);
    this.calculateFileGroupAssignments();
    this.filteredAssignedFiles = [...this.assignedFileGroups]
    this.filteredAvailableFiles = [...this.availableFileGroups]
    this.localHost = this.selectedRole.permissions.disLocalhostPermission;
    this.filterUserListSelectedRole(role);
  }

  setTab(selectedTab: string) {
    this.selectedTab = selectedTab;
  }

  filterUserListSelectedRole(role) {
    this.matchingUsers = this.users.filter(user => user.roles.some(userRole => userRole.id === role.id));
    this.nonMatchingUsers = this.users.filter(user => !user.roles.some(userRole => userRole.id === role.id));
  }

  setAllTablePermissionCollapse(isOpen: boolean): void {
    if (this.tableCollapsibles && this.tableCollapsibles.length > 0) {
      this.tableCollapsibles.forEach(c => {
        try {
          c.setOpen(isOpen);
        } catch (err) {
          this.logger.warn('AdminComponent', 'Failed to toggle table collapsible', {
            operation: 'setAllTablePermissionCollapse'
          }, err);
        }
      });
    } else {
      this.logger.debug('AdminComponent', 'No table collapsibles available to toggle', {
        operation: 'setAllTablePermissionCollapse'
      });
    }
  }

  setAllIntegrationPermissionCollapse(isOpen: boolean): void {
    if (this.integrationCollapsibles && this.integrationCollapsibles.length > 0) {
      this.integrationCollapsibles.forEach(c => {
        try {
          c.setOpen(isOpen);
        } catch (err) {
          this.logger.warn('AdminComponent', 'Failed to toggle integration collapsible', {
            operation: 'setAllIntegrationPermissionCollapse'
          }, err);
        }
      });
    } else {
      this.logger.debug('AdminComponent', 'No integration collapsibles available to toggle', {
        operation: 'setAllIntegrationPermissionCollapse'
      });
    }
  }

  //Enable + Disable user roles ===========================================================================================
  toggleUserRole(user: UserV2, role: Role) {
    let userRole = user.roles.find(userRole => userRole.id === role.id)
    if (userRole) {
      user.roles.splice(user.roles.indexOf(userRole), 1)
    } else {
      user.roles.push(new Role(role.id, new Date().toISOString(), role.expirationDate, this.userId));
    }
    this.addUserToUpdatedCollection(user);
    this.oneOwner = this.isOnlyOneOwner();
  }
  toggleUserExpirationDate(user: UserV2, role: Role) {
    const userRole = user.roles.find(r => r.id === role.id);
    if (!userRole) return;
    userRole.expirationDate = role.expirationDate;
    this.addUserToUpdatedCollection(user);
  }

  calculateFileGroupAssignments() {
    this.assignedFileGroups = [];
    this.availableFileGroups = [];
    this.fileGroups.forEach(f => {
      if (this.selectedRole.permissions.fileGroups.groups.some(roleFileGroup => roleFileGroup.name === f.id)) {
        this.assignedFileGroups.push(f);
      } else {
        this.availableFileGroups.push(f);
      }
    });
  }

  // Filter Users
  filterUserList(selectedRole = null) {
    const searchLower = this.searchStringUser
      .trim()
      .toLocaleLowerCase()
      .split(/\s+/);

    if (selectedRole) {
      this.matchingUsers = this.users.filter(user => {
        const firstName = user.firstName?.toLowerCase() ?? '';
        const lastName = user.lastName?.toLowerCase() ?? '';
        return user.roles.some(userRole => userRole.id === selectedRole.id) &&
          searchLower.every(word => firstName.includes(word) || lastName.includes(word));
      });

      this.nonMatchingUsers = this.users.filter(user => {
        const firstName = user.firstName?.toLowerCase() ?? '';
        const lastName = user.lastName?.toLowerCase() ?? '';
        return !user.roles.some(userRole => userRole.id === selectedRole.id) &&
          searchLower.every(word => firstName.includes(word) || lastName.includes(word));
      });
    } else {
      this.filteredUsers = this.users.filter(user => {
        const firstName = user.firstName?.toLowerCase() ?? '';
        const lastName = user.lastName?.toLowerCase() ?? '';
        return searchLower.every(word => firstName.includes(word) || lastName.includes(word));
      });
    }
    this.checkForOwners();
  }

  // Filter Roles
  filterRoleList() {
    const searchLower = this.searchStringRole.toLocaleLowerCase();
    this.filteredRoles = this.roles.filter((file) =>
      file.name.toLowerCase().includes(searchLower)
    )
  }
  // Filter Assigned Files
  filterAssignList() {
    const searchLower = this.searchStringAssign.toLocaleLowerCase();
    this.filteredAssignedFiles = this.assignedFileGroups.filter((file) =>
      file.name.toLowerCase().includes(searchLower))
  }
  // Filter Available Files
  filterAvailableList() {
    const searchLower = this.searchStringAvaliable.toLocaleLowerCase();
    this.filteredAvailableFiles = this.availableFileGroups.filter((file) =>
      file.name.toLowerCase().includes(searchLower))
  }
  //Enable + Disable File Groups ===========================================================================================
  toggleFileGroupSelection(fileGroup: FileGroup) {
    fileGroup.isSelected = fileGroup.isSelected != null ? !fileGroup.isSelected : true;
  }

  AssignSelectedFileGroupsToRole() {
    var selectedFileGroups = this.availableFileGroups.filter(fg => fg.isSelected != null && fg.isSelected == true)
    selectedFileGroups.forEach(fg => {
      fg.isSelected = false;
      this.availableFileGroups.splice(this.availableFileGroups.indexOf(fg), 1);
      this.assignedFileGroups.push(fg);
      this.selectedRole.permissions.fileGroups.groups.push(new FileGroupPermission(fg))
      this.adminService.addRoleToUpdatedCollection(this.selectedRole);
    });
    this.filteredAssignedFiles = this.assignedFileGroups
    this.filteredAvailableFiles = this.availableFileGroups;
  }

  RemoveSelectedFileGroupsFromRole() {
    var selectedFileGroups = this.assignedFileGroups.filter(fg => fg.isSelected != null && fg.isSelected == true)
    selectedFileGroups.forEach(fg => {
      fg.isSelected = false;
      this.assignedFileGroups.splice(this.assignedFileGroups.indexOf(fg), 1);
      this.availableFileGroups.push(fg);
      const groups = this.selectedRole.permissions.fileGroups.groups;
      const index = groups.findIndex(f => f.name === fg.id);
      if (index >= 0) groups.splice(index, 1);
      this.adminService.addRoleToUpdatedCollection(this.selectedRole);
    });
    this.filteredAssignedFiles = this.assignedFileGroups
    this.filteredAvailableFiles = this.availableFileGroups;
  }

  async addRole() {
    const newRoleName = this.getNextAvailableRoleName();
    const newRole = await this.adminService.createNewRole(newRoleName);

    if (!newRole) {
      this.toastService.showErrorToast('', this.getUnableToCreateRoleMessage());
      return;
    }

    this.roles.push(newRole);
    this.filteredRoles = this.roles;
  }

  async editRole() {
    if (!this.selectedRole || this.isOwnerRole(this.selectedRole)) {
      return;
    }

    if (this.isEditingRole(this.selectedRole)) {
      this.cancelRoleNameEdit();
      return;
    }

    this.enableEditing = true;
    this.editIndex = this.selectedRole.id;
    this.editRoleNameEnable = true;
    this.editRoleNameDraft = this.selectedRole.name ?? '';
  }

  toggleEditing(role) {
    if (this.isEditingRole(role)) {
      this.editRoleNameEnable = true;
    }
  }

  onRoleNameInputClick(event: MouseEvent, role: Role) {
    if (this.isEditingRole(role)) {
      event.stopPropagation();
      this.toggleEditing(role);
    }
  }

  onRoleNameInputChange(role: Role, value: string) {
    if (this.isEditingRole(role)) {
      this.editRoleNameDraft = value;
    }
  }

  getRoleNameInputValue(role: Role): string {
    return this.isEditingRole(role) ? this.editRoleNameDraft : role.name;
  }

  updateEdit(event?: MouseEvent) {
    event?.stopPropagation();

    const roleBeingEdited = this.getRoleBeingEdited();
    if (!roleBeingEdited) {
      this.cancelRoleNameEdit();
      return;
    }

    const proposedName = this.editRoleNameDraft;

    if (this.normalizeRoleName(proposedName).length === 0) {
      this.toastService.showErrorToast('', this.getRequiredRoleNameMessage());
      return;
    }

    if (this.isDuplicateRoleName(roleBeingEdited, proposedName)) {
      this.toastService.showErrorToast('', this.getDuplicateRoleNameMessage());
      return;
    }

    roleBeingEdited.name = proposedName.trim();
    this.filterRoleList();
    this.finishRoleNameEdit();
    this.adminService.addRoleToUpdatedCollection(roleBeingEdited);
  }

  checkForOwners() {
    var listOfOwners = [];
    this.users.forEach(user => user.roles.forEach(role => {
      if (role.id === "_Owner") {
        listOfOwners.push(role)
      }
    }));
    if (listOfOwners.length < 2) {
      this.oneOwner = true;
    } else {
      this.oneOwner = false;
    }
  }
  isOnlyOneOwner(): boolean {
    let ownerCount = 0;
    for (let user of this.users) {
      if (user.roles.some(role => role.id === '_Owner')) {
        ownerCount++;
      }
    }
    return ownerCount === 1;
  }
  private calculateEnabledRoles() {
    this.roles.forEach(r => r.enable = false);
    this.selectedUser.roles.forEach(userRole => {
      this.roles.forEach(r => {
        if (r.id === userRole.id) {
          r.enable = true;
          r.expirationDate = userRole.expirationDate;
        }
      });
    });
  }


  //Enable + Disable Permissions ===========================================================================================
  public toggleSecurityAdmin() {
    this.selectedRole.permissions.securityAdmin = !this.selectedRole.permissions.securityAdmin;
    this.adminService.addRoleToUpdatedCollection(this.selectedRole);
  }


  //Track Changes for Save Functionality ===========================================================================================
  public addUserToUpdatedCollection(user: UserV2) {
    if (!this.updatedUsers.some(u => u.id == user.id)) {
      this.updatedUsers.push(user);
    }
    this.showSaveButton = true;
  }

  async save() {
    // Check if save is already in progress
    if (this.isSaving) return;

    this.isSaving = true;

    try {
      // Save both roles and users
      await Promise.all([
        this.adminService.saveUpdatedRoles(),
        this.adminService.saveUpdatedUsers(this.updatedUsers)
      ]);

      // Update initial state only after successful save
      this.initialRoles = JSON.parse(JSON.stringify(this.roles));
      this.filteredUsers = [...this.users];

      // Clear updated collections and reset flags
      this.updatedUsers = [];
      this.updatedRoles = [];
      this.showSaveButton = false;

      // Notify user
      this.toastService.showSuccessToast('', 'Saving Complete!');
    } catch (error) {
      this.logger.error('AdminComponent', 'Save failed', error, {
        operation: 'save'
      });
      this.toastService.showErrorToast('', 'Save Failed!');
    } finally {
      this.isSaving = false;
    }
  }

  async startUserSync() {
    if (this.isSyncing) return;

    const dialogRef = this.dialog.open(ConfirmationDialogComponent, {
      data: {
        title: 'User Sync',
        message: 'Are you sure you want to start the user sync process?'
      }
    })

    dialogRef.afterClosed().subscribe(result => {
      if (result == 'confirmed') {
        this.isSyncing = true;
        // Notify user
        this.toastService.showInfoToast('', 'User Sync started. This may take some time.');

        // Save both roles and users
        this.adminService.syncUsers().then(success => {
          if (!success) {
            this.toastService.showErrorToast('', 'User Sync Failed!');
            return;
          }

          // Notify user
          this.toastService.showSuccessToast('', 'User Sync Complete!');
        }).catch(error => {
          this.logger.error('AdminComponent', 'User sync failed', error, {
            operation: 'startUserSync'
          });
          this.toastService.showErrorToast('', 'User Sync Failed!');
        }).finally(() => this.isSyncing = false);
      }
    })
  }

  //Helper methods ===========================================================================================

  public isEditingRole(role: Role | null): boolean {
    return !!role && this.enableEditing && this.editIndex === role.id;
  }

  public isDuplicateRoleName(role: Role | null, nameOverride?: string): boolean {
    if (!role) {
      return false;
    }

    const normalizedName = this.normalizeRoleName(nameOverride ?? role.name);

    if (!normalizedName) {
      return false;
    }

    return this.roles.some(existingRole =>
      existingRole.id !== role.id &&
      this.normalizeRoleName(existingRole.name) === normalizedName
    );
  }

  public isRoleNameDraftEmpty(role: Role | null): boolean {
    return !this.isOwnerRole(role) &&
      this.isEditingRole(role) &&
      this.normalizeRoleName(this.editRoleNameDraft).length === 0;
  }

  public isDuplicateRoleNameDraft(role: Role | null): boolean {
    return !this.isOwnerRole(role) &&
      this.isEditingRole(role) &&
      this.isDuplicateRoleName(role, this.editRoleNameDraft);
  }

  public isRoleNameDraftInvalid(role: Role | null): boolean {
    return this.isRoleNameDraftEmpty(role) || this.isDuplicateRoleNameDraft(role);
  }

  public getRoleNameValidationMessage(role: Role | null): string | null {
    if (this.isRoleNameDraftEmpty(role)) {
      return this.getRequiredRoleNameMessage();
    }

    if (this.isDuplicateRoleNameDraft(role)) {
      return this.getDuplicateRoleNameMessage();
    }

    return null;
  }

  private getRoleBeingEdited(): Role | null {
    if (this.editIndex) {
      return this.roles.find(role => role.id === this.editIndex) ?? null;
    }

    return this.selectedRole;
  }

  private cancelRoleNameEdit(): void {
    this.finishRoleNameEdit();
  }

  private finishRoleNameEdit(): void {
    this.editIndex = null;
    this.editRoleNameEnable = false;
    this.enableEditing = false;
    this.editRoleNameDraft = '';
  }

  private normalizeRoleName(name: string | null | undefined): string {
    return (name ?? '').trim().toLowerCase();
  }

  public isOwnerRole(role: Role | null): boolean {
    return role?.id === '_Owner';
  }

  private getAdminString(key: string): string {
    return (this.strings as Record<string, string | undefined>)?.[key] ?? '';
  }

  private getDuplicateRoleNameMessage(): string {
    return this.getAdminString('duplicateRoleName') || 'A role with this name already exists.';
  }

  private getRequiredRoleNameMessage(): string {
    return this.getAdminString('roleNameRequired') || 'Role name is required.';
  }

  private getUnableToCreateRoleMessage(): string {
    return this.getAdminString('unableToCreateRole') || 'Unable to create role.';
  }

  private toTablePermission(tableData: TableData): TablePermission {
    let t = new TablePermission();
    t.name = tableData.id;
    t.actualName = tableData.name;
    return t;
  }

  toggleLocalhost() {
    this.selectedRole.permissions.disLocalhostPermission = !this.selectedRole.permissions.disLocalhostPermission;
    this.showSaveButton = true;
    this.adminService.addRoleToUpdatedCollection(this.selectedRole);
  }

  //gray-outlist-list
  shouldGrayBackground(index: number): boolean {
    return index % 2 === 1;
  }

  filterPermissions() {
    if (this.searchStringPermission.length == 0) {
      this.filteredTables = [...this.tables];
      this.filteredIntegrations = [...this.integrations];
    }
    else {
      const searchLower = this.searchStringPermission.toLocaleLowerCase();
      this.filteredTables = this.tables.filter(table =>
        table.actualName.toLowerCase().includes(searchLower)
      );
      this.filteredIntegrations = this.integrations.filter(integration =>
        integration.name.toLowerCase().includes(searchLower)
      );
    }
  }

  private getNextAvailableRoleName(): string {
    const usedRoleNumbers = new Set(
      this.roles
        .map(role => this.normalizeRoleName(role?.name).match(/^role\s+(\d+)$/))
        .filter(match => match !== null)
        .map(match => Number(match![1]))
        .filter(roleNumber => Number.isFinite(roleNumber))
    );

    let nextRoleNumber = 1;

    while (usedRoleNumbers.has(nextRoleNumber)) {
      nextRoleNumber++;
    }

    return `Role ${nextRoleNumber}`;
  }
}
